import 'simplebar/src/simplebar.css'
import AppBar from '../components/app-bar'
import SideBar from '../components/side-bar'
import React, { useState, ErrorInfo, ReactElement } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import clsx from 'clsx'
import { styled } from '@mui/material/styles'

import {
  LayoutProps,
  AppBar as DefaultAppBar,
  Menu as DefaultMenu,
  Sidebar as DefaultSidebar,
  SkipNavigationButton,
  useSidebarState,
  Error,
} from 'react-admin'
import { Box } from '@mui/material'

const PREFIX = 'RaLayout'
const LayoutClasses = {
  appFrame: `${PREFIX}-appFrame`,
  contentWithSidebar: `${PREFIX}-contentWithSidebar`,
  appbarCollapsed: `${PREFIX}-appbarCollapsed`,
  content: `${PREFIX}-content`,
}

const StyledLayout = styled('div', {
  name: PREFIX,
  overridesResolver: (props, styles) => styles.root,
})(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  zIndex: 1,
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,
  position: 'relative',
  minWidth: 'fit-content',
  width: '100%',
  color: theme.palette.getContrastText(theme.palette.background.default),

  [`& .${LayoutClasses.appFrame}`]: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    marginTop: theme.spacing(6),
    transition: `margin ${theme.transitions.easing.easeOut} ${theme.transitions.duration.shortest}ms`,
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(7),
    },
  },
  [`& .${LayoutClasses.contentWithSidebar}`]: {
    display: 'flex',
    flexGrow: 1,
  },
  [`& .${LayoutClasses.content}`]: {
    backgroundColor: theme.palette.background.default,
    zIndex: 2,
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    flexBasis: 0,
    padding: `${theme.spacing(3)} ${theme.spacing(10)} ${theme.spacing(10)}`,
    // maxWidth: theme.breakpoints.values.xl,
    margin: '0 auto',
    width: '100%',
    [theme.breakpoints.up('lg')]: {
      paddingTop: theme.spacing(5),
    },
  },
}))

const BaseLayout: React.FC = (props: LayoutProps) => {
  const {
    appBar: AppBar = DefaultAppBar,
    children,
    className,
    dashboard,
    error: errorComponent,
    menu: Menu = DefaultMenu,
    sidebar: Sidebar = DefaultSidebar,
    title,
    ...rest
  } = props

  const [open] = useSidebarState()
  const [errorInfo, setErrorInfo] = useState<ErrorInfo | undefined>(undefined)

  const handleError: (error: Error, info: ErrorInfo) => void = (error: Error, info: ErrorInfo) => {
    setErrorInfo(info)
  }

  return (
    <StyledLayout className={clsx('layout', className)} {...rest}>
      <SkipNavigationButton />
      <Box className={LayoutClasses.appFrame}>
        <AppBar open={open} title={title} />
        <Box component='main' className={LayoutClasses.contentWithSidebar}>
          <Sidebar>
            <Menu hasDashboard={!!dashboard} />
          </Sidebar>
          <Box id='main-content' className={LayoutClasses.content}>
            <ErrorBoundary
              onError={handleError}
              fallbackRender={({ error, resetErrorBoundary }): ReactElement => (
                <Error
                  error={error}
                  errorComponent={errorComponent}
                  errorInfo={errorInfo}
                  resetErrorBoundary={resetErrorBoundary}
                  title={title}
                />
              )}
            >
              {children}
            </ErrorBoundary>
          </Box>
        </Box>
      </Box>
    </StyledLayout>
  )
}

const CustomLayout = (props): JSX.Element => (
  <BaseLayout {...props} appBar={AppBar} sidebar={SideBar} />
)

export default CustomLayout
